import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import { CheckCircle2, ArrowRight, Building2, DollarSign, Users } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function CompanyInsights() {
  return (
    <>
      <Helmet>
        <title>Company Insights - Research Companies & Salaries | LoopBaan.ai</title>
        <meta name="description" content="Get insider insights on companies, salaries, culture, and interview processes. Make informed decisions about where to apply." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <MegaMenuNav />

        <section className="relative overflow-hidden py-20 md:py-32 bg-gradient-to-br from-accent/10 via-background to-primary/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-accent/20 text-accent border-accent/30">RESEARCH</Badge>
              <h1 className="mb-6 text-5xl md:text-7xl font-bold leading-tight">
                Know Everything About{" "}
                <span className="bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">
                  The Company
                </span>
              </h1>
              <p className="mb-8 text-xl text-muted-foreground max-w-2xl mx-auto">
                Research salaries, company culture, interview questions, and employee reviews. Walk into every interview fully prepared.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/signup">
                  <Button size="lg" className="gap-2">
                    Start Researching <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
              {[
                {
                  icon: DollarSign,
                  title: "Salary Intelligence",
                  description: "See real salary ranges for specific roles at any company. Know your worth before negotiating."
                },
                {
                  icon: Users,
                  title: "Culture & Reviews",
                  description: "Read employee reviews, learn about work-life balance, management style, and company values."
                },
                {
                  icon: Building2,
                  title: "Company Profiles",
                  description: "Get detailed insights on company size, funding, growth trajectory, and hiring trends."
                }
              ].map((feature, i) => (
                <Card key={i} className="p-6 hover:shadow-lg transition-all">
                  <feature.icon className="h-12 w-12 text-accent mb-4" />
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center mb-12">What You'll Learn</h2>
              <div className="grid gap-6 md:grid-cols-2">
                {[
                  "Salary ranges by role and location",
                  "Employee satisfaction ratings",
                  "Common interview questions",
                  "Company culture and values",
                  "Work-life balance insights",
                  "Benefits and perks breakdown",
                  "Growth and promotion opportunities",
                  "Hiring timeline and process"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <span className="text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Make Informed Career Decisions
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Don't go into interviews blind. Research companies and know what to expect.
            </p>
            <Link to="/signup">
              <Button size="lg" className="gap-2">
                Start Researching <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
